/**
 * Hook for calculating thread information, lane assignments, and message indices.
 */

import { useMemo, useCallback } from "react";
import type { ShipmentEventApi } from "../../../../../src/shipments/types";
import type { LaneThread, ThreadInfo, OtherThreadSegment } from "../types";
import { DEFAULT_LANE_COLORS } from "../constants";

interface UseThreadCalculationsResult {
  /** All threads sorted by message count (descending) */
  threadInfo: ThreadInfo[];
  /** Top N threads assigned to colored lanes */
  laneThreads: LaneThread[];
  /** Map of threadId to LaneThread for quick lookup */
  laneThreadMap: Map<string, LaneThread>;
  /** Check if a thread is a lane thread */
  isLaneThread: (threadId: string | undefined) => boolean;
  /** Get lane info for a thread */
  getLaneInfo: (threadId: string | undefined) => LaneThread | undefined;
  /** First and last message indices per lane thread */
  threadMessageIndices: Map<string, { first: number; last: number }>;
  /** Groups of consecutive messages from same non-lane thread */
  otherThreadSegments: OtherThreadSegment[];
}

/**
 * Calculates thread information, lane assignments, and message indices.
 * All calculations are memoized for performance.
 *
 * @param events - The list of shipment events
 * @param filteredEvents - The filtered and sorted events list
 * @param numberOfLanes - Number of colored lanes (default: 4)
 * @param laneColors - Colors for the lanes (default: DEFAULT_LANE_COLORS)
 */
export function useThreadCalculations(
  events: ShipmentEventApi[],
  filteredEvents: ShipmentEventApi[],
  numberOfLanes: number = 4,
  laneColors: string[] = DEFAULT_LANE_COLORS
): UseThreadCalculationsResult {
  // Calculate unique threads with message counts
  const threadInfo = useMemo(() => {
    const threadMap = new Map<
      string,
      { subject: string; count: number; threadId: string }
    >();
    events.forEach((event) => {
      if (event.threadId) {
        const existing = threadMap.get(event.threadId);
        if (existing) {
          existing.count++;
        } else {
          threadMap.set(event.threadId, {
            subject: event.emailSubject || "No subject",
            count: 1,
            threadId: event.threadId,
          });
        }
      }
    });
    return Array.from(threadMap.values()).sort((a, b) => b.count - a.count);
  }, [events]);

  // Lane threads - top N threads with colors assigned
  const laneThreads: LaneThread[] = useMemo(() => {
    return threadInfo.slice(0, numberOfLanes).map((thread, index) => ({
      threadId: thread.threadId,
      subject: thread.subject,
      color: laneColors[index % laneColors.length],
      laneIndex: index,
      messageCount: thread.count,
    }));
  }, [threadInfo, numberOfLanes, laneColors]);

  // Map threadId to lane info for quick lookup
  const laneThreadMap = useMemo(() => {
    const map = new Map<string, LaneThread>();
    laneThreads.forEach((lane) => {
      map.set(lane.threadId, lane);
    });
    return map;
  }, [laneThreads]);

  // Helper to check if a thread is a lane thread
  const isLaneThread = useCallback(
    (threadId: string | undefined) => {
      return threadId ? laneThreadMap.has(threadId) : false;
    },
    [laneThreadMap]
  );

  // Get lane info for a thread
  const getLaneInfo = useCallback(
    (threadId: string | undefined) => {
      return threadId ? laneThreadMap.get(threadId) : undefined;
    },
    [laneThreadMap]
  );

  // Compute first/last message indices per lane thread
  const threadMessageIndices = useMemo(() => {
    const indices = new Map<string, { first: number; last: number }>();

    filteredEvents.forEach((event, index) => {
      if (event.threadId && isLaneThread(event.threadId)) {
        const existing = indices.get(event.threadId);
        if (existing) {
          existing.last = index;
        } else {
          indices.set(event.threadId, { first: index, last: index });
        }
      }
    });

    return indices;
  }, [filteredEvents, isLaneThread]);

  // Compute "other thread" segments - groups of consecutive messages from the same non-lane thread
  const otherThreadSegments = useMemo(() => {
    const segments: OtherThreadSegment[] = [];

    let currentSegment: OtherThreadSegment | null = null;

    filteredEvents.forEach((event, index) => {
      const isOther = event.threadId && !isLaneThread(event.threadId);

      if (isOther) {
        if (currentSegment && currentSegment.threadId === event.threadId) {
          // Continue current segment
          currentSegment.lastIndex = index;
        } else {
          // End previous segment if exists
          if (currentSegment) {
            segments.push(currentSegment);
          }
          // Start new segment
          currentSegment = {
            threadId: event.threadId!,
            subject: event.emailSubject || "No subject",
            firstIndex: index,
            lastIndex: index,
          };
        }
      } else {
        // Not an "other" thread message - end current segment if exists
        if (currentSegment) {
          segments.push(currentSegment);
          currentSegment = null;
        }
      }
    });

    // Don't forget the last segment
    if (currentSegment) {
      segments.push(currentSegment);
    }

    return segments;
  }, [filteredEvents, isLaneThread]);

  return {
    threadInfo,
    laneThreads,
    laneThreadMap,
    isLaneThread,
    getLaneInfo,
    threadMessageIndices,
    otherThreadSegments,
  };
}
